import {
  CreateTrainingRequest,
  CustomDatasetConfig,
  FieldConstraints,
  NormalizedTrainingJob,
  TrainingForm,
  TrainingJobFromResponse,
  TrainingMethod,
  TrainingStatus
} from "@/types/training";
import { toDuration } from "./date";
import env from "@/env";
import {
  ADDITIONAL_CONFIG_FIELD_MAP_REVERSE,
  ADDITIONAL_CONFIG_FIELDS_LABELS
} from "@/components/Intelligence/Training/constants";

export const normalizeJob = (job: TrainingJobFromResponse): NormalizedTrainingJob => {
  return {
    id: job.id,
    method: job.ft_type as TrainingMethod,
    hfDataset: job.dataset_hf,
    hfModel: job.base_model_hf,
    userId: job.user_id,
    exportedHfRepo: job.exported_hf_repo,
    status: job.status.unified_status as TrainingStatus,
    remainingTimeHours: job.status.remaining_time_hours,
    progress: job.status.progress,
    createdAt: job.created_at,
    endTime: job.end_time,
    endedAt: job.job_finished_at,
    additionalTrainingConfig:
      Object.keys(job.additional_training_config || {}).length > 0
        ? {
            learningRate: job.additional_training_config?.learning_rate,
            epochs: job.additional_training_config?.num_train_epochs,
            maxGradientNorm: job.additional_training_config?.max_grad_norm,
            maxSamples: job.additional_training_config?.max_samples,
            computeType:
              job.additional_training_config?.bf16 === true
                ? "bf16"
                : job.additional_training_config?.fp16 === true
                  ? "fp16"
                  : undefined,
            cutoffLength: job.additional_training_config?.cutoff_len,
            batchSize: job.additional_training_config?.per_device_train_batch_size,
            gradientAccumulationSteps: job.additional_training_config?.gradient_accumulation_steps,
            valSize: job.additional_training_config?.val_size,
            lrScheduler: job.additional_training_config?.lr_scheduler_type,
            loggingSteps: job.additional_training_config?.logging_steps,
            saveSteps: job.additional_training_config?.save_steps,
            warmupSteps: job.additional_training_config?.warmup_steps,
            nefTuneAlpha: job.additional_training_config?.neftune_noise_alpha,
            extraArguments: job.additional_training_config?.extra_args,
            packSequence: job.additional_training_config?.packing,
            useNeatPacking: job.additional_training_config?.neat_packing,
            trainOnPrompt: job.additional_training_config?.train_on_prompt,
            maskHistory: job.additional_training_config?.mask_history,
            resizeTokenEmbeddings: job.additional_training_config?.resize_vocab,
            enableLlamaPro: job.additional_training_config?.use_llama_pro,
            externalLogger: job.additional_training_config?.report_to,
            trainableLayers: job.additional_training_config?.freeze_trainable_layers,
            trainableModules: job.additional_training_config?.freeze_trainable_modules,
            extraModules: job.additional_training_config?.freeze_extra_modules,
            loraRank: job.additional_training_config?.lora_rank,
            loraAlpha: job.additional_training_config?.lora_alpha,
            loraDropout: job.additional_training_config?.lora_dropout,
            loraLrRatio: job.additional_training_config?.loraplus_lr_ratio,
            createNewAdapter: job.additional_training_config?.create_new_adapter,
            useRslora: job.additional_training_config?.use_rslora,
            useDora: job.additional_training_config?.use_dora,
            usePissa: job.additional_training_config?.pissa_init,
            loraModules: job.additional_training_config?.lora_target,
            loraAdditionalModules: job.additional_training_config?.additional_target,
            betaValue: job.additional_training_config?.pref_beta,
            ftxGamma: job.additional_training_config?.pref_ftx,
            lossType: job.additional_training_config?.pref_loss,
            scoreNorm: job.additional_training_config?.ppo_score_norm,
            whitenRewards: job.additional_training_config?.ppo_whiten_rewards,
            useGaLore: job.additional_training_config?.use_galore,
            galoreRank: job.additional_training_config?.galore_rank,
            galoreUpdateInterval: job.additional_training_config?.galore_update_interval,
            galoreScale: job.additional_training_config?.galore_scale,
            galoreModules: job.additional_training_config?.galore_target,
            useApollo: job.additional_training_config?.use_apollo,
            apolloRank: job.additional_training_config?.apollo_rank,
            apolloUpdateInterval: job.additional_training_config?.apollo_update_interval,
            apolloScale: job.additional_training_config?.apollo_scale,
            apolloModules: job.additional_training_config?.apollo_target,
            useBAdam: job.additional_training_config?.use_badam,
            badamMode: job.additional_training_config?.badam_mode,
            switchMode: job.additional_training_config?.badam_switch_mode,
            switchInterval: job.additional_training_config?.badam_switch_interval,
            updateRatio: job.additional_training_config?.badam_update_ratio,
            useSwanLab: job.additional_training_config?.use_swanlab,
            swanLabProject: job.additional_training_config?.swanlab_project,
            swanLabExperimentName: job.additional_training_config?.swanlab_run_name,
            swanLabWorkspace: job.additional_training_config?.swanlab_workspace,
            swanLabAPIKey: job.additional_training_config?.swanlab_api_key,
            swanLabMode: job.additional_training_config?.swanlab_mode
          }
        : {},
    customDatasetConfig: job.custom_dataset_config || null
  };
};

/**
 * Extracts the repository ID from a Hugging Face URL.
 * e.g., "https://huggingface.co/google/gemma-3n-E4B-it" -> "google/gemma-3n-E4B-it"
 *
 * @param {string} url The Hugging Face URL to parse.
 * @returns {string|null} The repository ID or null if the URL is invalid.
 */
export function extractRepoIdFromUrl(url: string) {
  try {
    const { pathname } = new URL(url);
    // Remove leading/trailing slashes and split
    const pathParts = pathname.replace(/^\/+|\/+$/g, "").split("/");
    // For URLs like /datasets/fka/awesome-chatgpt-prompts or /fka/awesome-chatgpt-prompts
    // Find the last two segments (org/user and repo name)
    if (pathParts.length >= 2) {
      return `${pathParts[pathParts.length - 2]}/${pathParts[pathParts.length - 1]}`;
    }
    return url;
  } catch (error) {
    return url;
  }
}

export const formatStatus = (status: TrainingStatus | string): string =>
  status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getAdditionalTrainingConfig = (values: TrainingForm) => {
  const additionalConfig: Record<string, string | number | boolean | null | undefined> = {};

  if (values.trainingStyle === "simple") {
    return additionalConfig;
  }

  // basic configuration fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("basic")) {
    // section 1
    additionalConfig.learning_rate = values.learningRate;
    additionalConfig.num_train_epochs = values.epochs;
    additionalConfig.max_grad_norm = values.maxGradientNorm;
    additionalConfig.max_samples = values.maxSamples;
    if (values.computeType) {
      additionalConfig[values.computeType] = true;
    }

    // section 2
    additionalConfig.cutoff_len = values.cutoffLength;
    additionalConfig.per_device_train_batch_size = values.batchSize;
    additionalConfig.gradient_accumulation_steps = values.gradientAccumulationSteps;
    additionalConfig.val_size = values.valSize;
    additionalConfig.lr_scheduler_type = values.lrScheduler;
  }

  // extra configurations
  if (env.TAAS_ADVANCED_CONFIGS.includes("extra")) {
    additionalConfig.logging_steps = values.loggingSteps;
    additionalConfig.save_steps = values.saveSteps;
    additionalConfig.warmup_steps = values.warmupSteps;
    additionalConfig.neftune_noise_alpha = values.nefTuneAlpha;
    additionalConfig.extra_args = values.extraArguments;
    additionalConfig.packing = values.packSequence;
    additionalConfig.neat_packing = values.useNeatPacking;
    additionalConfig.train_on_prompt = values.trainOnPrompt;
    additionalConfig.mask_history = values.maskHistory;
    additionalConfig.resize_vocab = values.resizeTokenEmbeddings;
    additionalConfig.use_llama_pro = values.enableLlamaPro;
    additionalConfig.report_to = values.externalLogger;
  }

  // freeze tuning specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("freeze")) {
    additionalConfig.freeze_trainable_layers = values.trainableLayers;
    additionalConfig.freeze_trainable_modules = values.trainableModules;
    additionalConfig.freeze_extra_modules = values.extraModules;
  }

  // lora specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("lora")) {
    additionalConfig.lora_rank = values.loraRank;
    additionalConfig.lora_alpha = values.loraAlpha;
    additionalConfig.lora_dropout = values.loraDropout;
    additionalConfig.loraplus_lr_ratio = values.loraLrRatio;
    additionalConfig.create_new_adapter = values.createNewAdapter;
    additionalConfig.use_rslora = values.useRslora;
    additionalConfig.use_dora = values.useDora;
    additionalConfig.pissa_init = values.usePissa;
    additionalConfig.lora_target = values.loraModules;
    additionalConfig.additional_target = values.loraAdditionalModules;
  }

  // rlhf specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("rlhf")) {
    additionalConfig.pref_beta = values.betaValue;
    additionalConfig.pref_ftx = values.ftxGamma;
    additionalConfig.pref_loss = values.lossType;
    additionalConfig.ppo_score_norm = values.scoreNorm;
    additionalConfig.ppo_whiten_rewards = values.whitenRewards;
  }

  // galore specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("galore")) {
    additionalConfig.use_galore = values.useGaLore;
    additionalConfig.galore_rank = values.galoreRank;
    additionalConfig.galore_update_interval = values.galoreUpdateInterval;
    additionalConfig.galore_scale = values.galoreScale;
    additionalConfig.galore_target = values.galoreModules;
  }

  // apollo specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("apollo")) {
    additionalConfig.use_apollo = values.useApollo;
    additionalConfig.apollo_rank = values.apolloRank;
    additionalConfig.apollo_update_interval = values.apolloUpdateInterval;
    additionalConfig.apollo_scale = values.apolloScale;
    additionalConfig.apollo_target = values.apolloModules;
  }

  // badam specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("badam")) {
    additionalConfig.use_badam = values.useBAdam;
    additionalConfig.badam_mode = values.badamMode;
    additionalConfig.badam_switch_mode = values.switchMode;
    additionalConfig.badam_switch_interval = values.switchInterval;
    additionalConfig.badam_update_ratio = values.updateRatio;
  }

  // swanlab specific fields
  if (env.TAAS_ADVANCED_CONFIGS.includes("swanlab")) {
    additionalConfig.use_swanlab = values.useSwanLab;
    additionalConfig.swanlab_project = values.swanLabProject;
    additionalConfig.swanlab_run_name = values.swanLabExperimentName;
    additionalConfig.swanlab_workspace = values.swanLabWorkspace;
    additionalConfig.swanlab_api_key = values.swanLabAPIKey;
    additionalConfig.swanlab_mode = values.swanLabMode;
  }
  return additionalConfig;
};

export const getCustomDatasetConfig = (values: TrainingForm) => {
  const config: CustomDatasetConfig = {
    name: values.customDatasetName,
    dataset_info: {
      hf_hub_url: values.hf_hub_url === "" ? undefined : values.hf_hub_url,
      ms_hub_url: values.ms_hub_url === "" ? undefined : values.ms_hub_url,
      script_url: values.script_url === "" ? undefined : values.script_url,
      cloud_file_name: values.cloud_file_name === "" ? undefined : values.cloud_file_name,
      file_name: values.file_name === "" ? undefined : values.file_name
    }
  };

  if (values.customDatasetRanking) {
    config.dataset_info.ranking = values.customDatasetRanking;
  }

  if (values.customDatasetFormatting) {
    config.dataset_info.formatting = values.customDatasetFormatting;
  }

  if (values.customDatasetSubset) {
    config.dataset_info.subset = values.customDatasetSubset;
  }

  if (values.customDatasetFolder) {
    config.dataset_info.folder = values.customDatasetFolder;
  }

  if (values.customDatasetSplit) {
    config.dataset_info.split = values.customDatasetSplit;
  }

  if (values.customDatasetSamples) {
    config.dataset_info.num_samples = values.customDatasetSamples;
  }

  if (values.columns) {
    config.dataset_info.columns = values.columns.reduce<Record<string, string | null>>(
      (acc, col: { key: string; value: string | null }) => {
        if (col.key && col.value) {
          acc[col.key] = col.value === "None" ? null : col.value;
        }
        return acc;
      },
      {}
    );
  }
  if (values.tags) {
    config.dataset_info.tags = values.tags.reduce<Record<string, string | null>>(
      (acc, tag: { key: string; value: string | null }) => {
        if (tag.key && tag.value) {
          acc[tag.key] = tag.value === "None" ? null : tag.value;
        }
        return acc;
      },
      {}
    );
  }

  return config;
};

export const getCreateTrainingRequestBody = (values: TrainingForm) => {
  const requestBody: CreateTrainingRequest = {
    base_model_hf: extractRepoIdFromUrl(values.model),
    dataset_hf: extractRepoIdFromUrl(values.dataset).split("/").pop() as string,
    ft_type: values.method,
    hf_token: values.hfToken?.trim() || undefined,
    additional_training_config: getAdditionalTrainingConfig(values)
  };

  if (values.datasetOption === "customDatasetOption") {
    requestBody.custom_dataset_config = getCustomDatasetConfig(values);
    requestBody.dataset_hf = requestBody.custom_dataset_config.name!;
  }
  return requestBody;
};

export const getRunTime = (createdAt: string, endedAt?: string | null): string => {
  const start = new Date(createdAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diff = end.getTime() - start.getTime();
  const runTime = toDuration(diff) || "-";
  return runTime;
};

export const isJobFinished = (job: NormalizedTrainingJob): boolean => {
  return (
    job.status === "completed" ||
    job.status === "failed" ||
    job.status === "deploy_failed" ||
    job.status === "training_failed"
  );
};

export const isJobFailed = (job: NormalizedTrainingJob) =>
  !["completed", "training", "deploying"].includes(job.status);

export const normalizeBaseModel = (model: {
  display_name: string;
  hf_link: string;
}): { value: string; label: string } => {
  return {
    value: model.hf_link,
    label: model.display_name
  };
};

export const addRequestToHubSpot = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  model: string;
  dataset: string;
  method: string;
  intent?: string;
  contactInfo?: string;
}): Promise<boolean> => {
  try {
    const body: {
      firstName: string;
      lastName: string;
      email: string;
      model: string;
      dataset: string;
      method: string;
      intent?: string;
      contactInfo?: string;
    } = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      method: data.method,
      model: data.model,
      dataset: data.dataset
    };

    if (data.intent) {
      body.intent = data.intent;
    }
    if (data.contactInfo) {
      body.contactInfo = data.contactInfo;
    }

    const response = await fetch("/api/submit-training-request", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    console.log("Request added to HubSpot successfully:", result);
    return true;
  } catch (error) {
    console.error("Failed to add request to HubSpot:", error);
    return false;
  }
};

// Utility to generate validation function
export function getValidationFn(constraints: FieldConstraints) {
  return (value: string) => {
    if (constraints.required && !value) return "Required";
    if (!value && !constraints.required) return undefined;

    if (constraints.type === "number") {
      const num = Number(value);
      if (isNaN(num)) return "Must be a number";
      if (constraints.integer && !Number.isInteger(num)) return "Must be an integer";

      if (constraints.min !== undefined && num < constraints.min)
        return `Minimum is ${constraints.min}`;
      if (constraints.max !== undefined && num > constraints.max)
        return `Maximum is ${constraints.max}`;
    }

    if (constraints.type === "json") {
      try {
        JSON.parse(value);
      } catch {
        return "Invalid JSON format";
      }
    }
    return undefined;
  };
}

export function getFieldErrorObject(details: { loc: string[]; msg: string }[]) {
  let errorMessage = "";
  const errorObject: Record<string, string> = {};
  details.forEach((err: { loc: string[]; msg: string }) => {
    const fieldName = err.loc[err.loc.length - 1];
    errorObject[ADDITIONAL_CONFIG_FIELD_MAP_REVERSE[fieldName]] = err.msg;
    errorMessage += `[FIELD_ERROR] ${ADDITIONAL_CONFIG_FIELDS_LABELS[ADDITIONAL_CONFIG_FIELD_MAP_REVERSE[fieldName]]}: ${err.msg}`;
  });

  return { errorMessage, errorObject };
}

// Utility to generate tooltip content
export function getTooltipContent(
  constraints: FieldConstraints
  // type: "input" | "select" = "input"
) {
  // if (type === "select") {
  //   return (
  //     <>
  //       <div>{constraints.description}</div>
  //       <ul className="mt-1 list-disc pl-4 text-xs">
  //         {constraints.options?.map((option) => (
  //           <li key={option.value}>
  //             <span className="font-semibold">{option.label}</span>
  //           </li>
  //         ))}
  //       </ul>
  //     </>
  //   );
  // }
  return (
    <>
      <div>{constraints.description}</div>
      <ul className="mt-1 list-disc pl-4 text-xs">
        {constraints.required ? <li>Required</li> : <li>Optional</li>}
        {constraints.type === "number" && <li>Must be a number</li>}
        {constraints.integer && <li>Must be an integer</li>}
        {constraints.min !== undefined && <li>Minimum: {constraints.min}</li>}
        {constraints.max !== undefined && <li>Maximum: {constraints.max}</li>}
      </ul>
    </>
  );
}
